import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DashboardNewUsersVendorsTable from "../../component/common/customTable/DashboardNewUsersVendorsTable";
import MonthlyJobsChart from "../../component/MonthlyJobsChart/MonthlyJobsChart";
import { getDashboardDataAsync } from "../../services/AdminServiceManage";
import { asyncStatus } from "../../utils/asyncStatus";
import { themeBlue, themeOrange } from "../../utils/colorTheme";
import { FiUsers } from "react-icons/fi";
import { MdOutlineWork, MdOutlineAccessTime, MdOutlineCheckCircle, MdOutlinePayment } from "react-icons/md";
import { HiOutlineUserGroup } from "react-icons/hi";

const DashboardHome = () => {
  const { get_dashboard_data_status, get_dashboard_data_data } = useSelector(
    (state) => state.admin_manage
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getDashboardDataAsync());
  }, []);

  if (get_dashboard_data_status === asyncStatus.LOADING) {
    return (
      <Stack alignItems="center" justifyContent="center" height="60vh">
        <CircularProgress size={30} sx={{ color: themeOrange }} />
      </Stack>
    );
  }

  const data = get_dashboard_data_data?.data;

  const kpiCards = [
    { label: "Pros", value: data?.host || 0, icon: <HiOutlineUserGroup size={22} />, bg: "#EDE7F6", color: "#5E35B1" },
    { label: "Users", value: data?.user || 0, icon: <FiUsers size={20} />, bg: "#E3F2FD", color: "#1565C0" },
    { label: "Total Orders", value: data?.total_orders || 0, icon: <MdOutlineWork size={22} />, bg: "#E8F5E9", color: "#2E7D32" },
    { label: "Pending", value: data?.pending_orders || 0, icon: <MdOutlineAccessTime size={22} />, bg: "#FFF8E1", color: "#F9A825" },
    { label: "Completed", value: data?.completed_orders || 0, icon: <MdOutlineCheckCircle size={22} />, bg: "#F3E5F5", color: "#7B1FA2" },
    { label: "Paid", value: data?.paid_orders || 0, icon: <MdOutlinePayment size={22} />, bg: "#E0F2F1", color: "#00897B" },
  ];

  return (
    <Stack sx={{ bgcolor: "#F8F9FA", minHeight: "100vh" }}>
      {/* Header */}
      <Stack sx={{ px: 3, py: 2.5 }}>
        <Typography sx={{ fontSize: "24px", fontWeight: 700, color: themeBlue }}>
          Dashboard
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "#999", mt: 0.3 }}>
          Overview of your platform
        </Typography>
      </Stack>

      <Container sx={{ maxWidth: { xl: "xl", lg: "lg", md: "md", sm: "sm", xs: "xs" } }}>
        {/* KPI Cards */}
        <Grid container spacing={2} mb={3}>
          {kpiCards.map((card, i) => (
            <Grid item xs={6} sm={4} md={2} key={i}>
              <Stack
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "14px",
                  p: 2.5,
                  boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
                  transition: "all 0.2s",
                  "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.1)", transform: "translateY(-2px)" },
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "10px",
                    bgcolor: card.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: card.color,
                    mb: 1.5,
                  }}
                >
                  {card.icon}
                </Box>
                <Typography sx={{ fontSize: "26px", fontWeight: 700, color: themeBlue, lineHeight: 1.1 }}>
                  {card.value}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#999", fontWeight: 500, mt: 0.5 }}>
                  {card.label}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>

        {/* Monthly Chart */}
        <Stack
          sx={{
            bgcolor: "#fff",
            borderRadius: "14px",
            boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
            p: 3,
            mb: 3,
          }}
        >
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: themeBlue, mb: 2 }}>
            Monthly Jobs Overview
          </Typography>
          <MonthlyJobsChart
            monthlyData={data?.monthly_order_graph}
            themeBlue={themeBlue}
            themeOrange={themeOrange}
          />
        </Stack>

        {/* Recent Orders */}
        <Stack
          sx={{
            bgcolor: "#fff",
            borderRadius: "14px",
            boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
            overflow: "hidden",
            mb: 3,
          }}
        >
          <Stack sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: themeBlue }}>
              Recent Orders
            </Typography>
          </Stack>
          {data?.recent_orders?.length > 0 ? (
            <Box sx={{ overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Order ID", "Customer", "Service", "Status", "Date"].map((col) => (
                      <th
                        key={col}
                        style={{
                          padding: "10px 16px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#999",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.recent_orders.slice(0, 5).map((order, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                      <td style={{ padding: "12px 16px", fontSize: "13px", fontWeight: 600, color: themeBlue }}>
                        #{order?.id}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: "#555" }}>
                        {order?.user?.first_name} {order?.user?.last_name}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: "#555" }}>
                        {order?.service?.name}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <span
                          style={{
                            padding: "4px 12px",
                            borderRadius: "8px",
                            fontSize: "11px",
                            fontWeight: 600,
                            backgroundColor:
                              order?.status === "Completed" ? "#E8F5E9" :
                              order?.status === "Pending" || order?.status === "Assigned" ? "#FFF8E1" :
                              "#E3F2FD",
                            color:
                              order?.status === "Completed" ? "#2E7D32" :
                              order?.status === "Pending" || order?.status === "Assigned" ? "#F9A825" :
                              "#1565C0",
                          }}
                        >
                          {order?.status}
                        </span>
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "12px", color: "#999" }}>
                        {new Date(order?.created_at).toLocaleDateString("en-US", {
                          month: "short", day: "numeric", year: "numeric",
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          ) : (
            <Stack alignItems="center" justifyContent="center" py={5}>
              <Typography sx={{ color: "#bbb", fontSize: "14px" }}>No recent orders</Typography>
            </Stack>
          )}
        </Stack>

        {/* New Users/Vendors */}
        <Stack
          sx={{
            bgcolor: "#fff",
            borderRadius: "14px",
            boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
            overflow: "hidden",
            mb: 4,
          }}
        >
          <Stack sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: themeBlue }}>
              New Users & Pros This Week
            </Typography>
          </Stack>
          {data?.users?.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" py={5}>
              <Typography sx={{ color: "#bbb", fontSize: "14px" }}>
                No new users or pros this week
              </Typography>
            </Stack>
          ) : (
            <Box sx={{ minWidth: "478px" }}>
              <DashboardNewUsersVendorsTable />
            </Box>
          )}
        </Stack>
      </Container>
    </Stack>
  );
};

export default DashboardHome;
